import { useMemo, useState } from "react"

import cn from "classnames"

import { SvgIcon } from "@components/common"
import { useTeeupPeopleStore } from "stores/teeupPeopleStore"

import ParticipantIcon from "../../Participants/ParticipantItem/ParticipantIcon"
import {
  formatZoneDate,
  formatZoneTime,
  getDisplayName,
  getZoneAbbreviation,
  getZoneCountry,
} from "../timezoneDisplay"

import OffsetIndicator from "./OffsetIndicator"

import "./index.scss"

// Spec: Multi-TZ Sync Up Detail Group (Notion e8f3d605c41448f98d39588e55f18c8e) -- one row per
// distinct participant timezone (or the "Unknown" bucket). Purpose-built for the People card;
// the legacy `Content/Timezone/TimezoneItem` is left untouched because it is still shared with
// the Suggest-When flow (SuggestWhenModalNd/TimeWheelTab/TimeWheel/GroupInfo), which is out of
// scope for this change.
const AVATAR_LIMIT = 9

const TimezoneRow = ({
  timezone,
  people,
  offsetHours = 0,
  isViewerZone,
  viewerTimezone,
  now,
  isUnknown,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const setCurrentParticipant = useTeeupPeopleStore(
    state => state.setCurrentParticipant
  )
  const setIsParticipantDetailsModalOpen = useTeeupPeopleStore(
    state => state.setIsParticipantDetailsModalOpen
  )

  // Spec: "clicking a participant row opens the existing participant-detail popup" -- same
  // mechanism the People roster's avatars already use (Content/Participants, TitleContent).
  const openParticipantDetails = (participant, e) => {
    e.stopPropagation()
    setCurrentParticipant(participant)
    setIsParticipantDetailsModalOpen(true)
  }

  // Spec: "up to 9, alphabetical by full name, then a +N overflow count" (avatars) and
  // "ALL its participants sorted by full name" (expanded list).
  const sortedPeople = useMemo(
    () =>
      [...(people || [])].sort((a, b) =>
        getDisplayName(a).localeCompare(getDisplayName(b))
      ),
    [people]
  )

  const visiblePeople = sortedPeople.slice(0, AVATAR_LIMIT)
  const overflowCount = sortedPeople.length - visiblePeople.length

  const zoneTime = !isUnknown ? now.tz(timezone) : null
  const abbreviation = isUnknown ? "Unknown" : getZoneAbbreviation(timezone, now)
  const country = isUnknown ? null : getZoneCountry(timezone)
  const viewerAbbreviation =
    !isUnknown && !isViewerZone ? getZoneAbbreviation(viewerTimezone, now) : null

  return (
    <div
      className={cn("people-timezone-row", { "is-unknown": isUnknown })}
      data-testid={`teeup-detail-timezone-row-${timezone}`}>
      <div
        className="people-timezone-row-summary"
        onClick={() => setIsExpanded(value => !value)}>
        <div className="people-timezone-row-zone">
          <div className="people-timezone-row-abbrev">{abbreviation}</div>
          {!isUnknown && (
            <div className="people-timezone-row-country">
              {country.flag ? `${country.flag} ` : ""}
              {country.name}
              {!isViewerZone && (
                <span className="people-timezone-row-offset-label">
                  {" "}
                  ({viewerAbbreviation} {offsetHours >= 0 ? "+" : ""}
                  {offsetHours})
                </span>
              )}
            </div>
          )}
        </div>

        {!isUnknown && (
          <div className="people-timezone-row-time">
            <div className="title">{formatZoneTime(zoneTime)}</div>
            <div className="subtitle">{formatZoneDate(zoneTime)}</div>
          </div>
        )}

        <div className="people-timezone-row-avatars">
          {visiblePeople.map(participant => (
            <ParticipantIcon
              key={participant.id}
              avatar={participant.avatar}
              defaultAvatar={getDisplayName(participant)}
            />
          ))}
          {overflowCount > 0 && (
            <div className="people-timezone-row-overflow">+{overflowCount}</div>
          )}
        </div>

        <SvgIcon
          name="arrow-2"
          color="var(--gray4)"
          className={cn("people-timezone-row-chevron", {
            "is-expanded": isExpanded,
          })}
        />
      </div>

      {!isUnknown && <OffsetIndicator offsetHours={offsetHours} />}

      {/* Spec item 4 (People Card expand/collapse animation): this used to conditionally mount
          the whole <ul> on isExpanded, an abrupt appear/disappear with no transition. Same
          grid-template-rows 0fr->1fr technique as ui/Collapse (index.scss) -- always mounted,
          height driven by the grid track so it animates instead of jumping. */}
      <div
        className={cn("people-timezone-row-participants-track", {
          "is-expanded": isExpanded,
        })}>
        <ul className="people-timezone-row-participants">
          {sortedPeople.map(participant => (
            <li
              key={participant.id}
              className="people-timezone-row-participant"
              onClick={e => openParticipantDetails(participant, e)}>
              <ParticipantIcon
                avatar={participant.avatar}
                defaultAvatar={getDisplayName(participant)}
              />
              <div className="people-timezone-row-participant-info">
                <div className="people-timezone-row-participant-name">
                  {getDisplayName(participant)}
                </div>
                {/* Spec: "each showing their profile hometown if set (omit when unset)". */}
                {participant.primaryLocation && (
                  <div className="people-timezone-row-participant-hometown">
                    {participant.primaryLocation}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default TimezoneRow
